import { TourProvider } from '@reactour/tour'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import history from 'history.js'
import moment from 'moment'
import { SnackbarProvider } from 'notistack'
import { Provider } from 'react-redux'
import { Router } from 'react-router-dom'
import '../styles/_app.scss'
import I18nConnectedProvider from '../translation/I18nConnectedProvider'
import AppContext from './appContext'
import Auth from './auth/Auth'
import AuthGuard from './auth/AuthGuard'
import { GlobalQueriesContextProvider } from './GlobalQueriesProvider'
import InactivityDetector from './InactivityDetector'
import AppLayout from './layout/AppLayout'
import { Store } from './redux/Store'
import routes from './RootRoutes'
import { CHTCtheme } from './theme/CHTCtheme'
import { CustomSnackbar } from './views/common-components/CustomSnackbars'

// TODO: Move to switching backends at some point
export const ACTIVE_BACKEND = 'Salesforce'
const queryClient = new QueryClient()

// Maintenance period configuration
const CLOUSURE_START = null
const MAINTENANCE_DIALOG_START_DATE = null
const MAINTENANCE_START = null
const MAINTENANCE_END = null

// lock site for maintenance
moment.updateLocale('iu', {
  months: [
    'ᔭᓄᐊᕆ',
    'ᕕᕗᐊᕆ',
    'ᒫᔾᔨ',
    'ᐊᐃᕆᓕ',
    'ᒪᐃ',
    'ᔫᓂ',
    'ᔪᓚᐃ',
    'ᐊᐅᒍᓯ',
    'ᓯᑎᐱᕆ',
    'ᐅᑐᐱᕆ',
    'ᓄᕕᐱᕆ',
    'ᑎᓯᐱᕆ'
  ],
  monthsShort: [
    'ᔭᓄᐊᕆ',
    'ᕕᕗᐊᕆ',
    'ᒫᔾᔨ',
    'ᐊᐃᕆᓕ',
    'ᒪᐃ',
    'ᔫᓂ',
    'ᔪᓚᐃ',
    'ᐊᐅᒍᓯ',
    'ᓯᑎᐱᕆ',
    'ᐅᑐᐱᕆ',
    'ᓄᕕᐱᕆ',
    'ᑎᓯᐱᕆ'
  ],
  weekdays: ['ᓈᑦᑏᖑᔭᖅ', 'ᓇᒡᒐᔾᔭᐅ', 'ᐊᐃᑉᐹ', 'ᐱᖓᔪᐊ', 'ᕿᑎᖅᑰ', 'ᐅᓪᓗᑐᐃᓐᓇᖅ', 'ᓯᕙᑖᕐᕕᒃ'],
  weekdaysShort: ['ᓈᑦᑏᖑᔭᖅ', 'ᓇᒡᒐᔾᔭᐅ', 'ᐊᐃᑉᐹ', 'ᐱᖓᔪᐊ', 'ᕿᑎᖅᑰ', 'ᐅᓪᓗᑐᐃᓐᓇᖅ', 'ᓯᕙᑖᕐᕕᒃ']
})
moment.updateLocale('fr', {
  months: [
    'Janvier',
    'Février',
    'Mars',
    'Avril',
    'Mai',
    'Juin',
    'Juillet',
    'Août',
    'Septembre',
    'Octobre',
    'Novembre',
    'Décembre'
  ],
  monthsShort: [
    'Janvier',
    'Février',
    'Mars',
    'Avril',
    'Mai',
    'Juin',
    'Juillet',
    'Août',
    'Septembre',
    'Octobre',
    'Novembre',
    'Décembre'
  ],
  weekdaysShort: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi']
})
moment.updateLocale('en', { week: { dow: 1 } })
moment.updateLocale('en', {
  monthsShort: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ],
  weekdaysShort: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
})

const App = () => {
  return (
    <AppContext.Provider value={{ routes }}>
      <Provider store={Store}>
        <I18nConnectedProvider>
          <QueryClientProvider client={queryClient}>
            <SnackbarProvider
              Components={{
                success: CustomSnackbar,
                error: CustomSnackbar,
                warning: CustomSnackbar,
                info: CustomSnackbar
              }}
              maxSnack={3}
              autoHideDuration={2000}
            >
              <CHTCtheme>
                <TourProvider
                  steps={[]}
                  onClickClose={({ setIsOpen, setCurrentStep }) => {
                    setIsOpen(false)
                    setCurrentStep(0)
                  }}
                  styles={{
                    popover: (base) => ({
                      ...base,
                      '--reactour-accent': '#5cb7b7'
                    })
                  }}
                >
                  <Router history={history}>
                    <Auth
                      maintenance={{
                        startDate: MAINTENANCE_START,
                        endDate: MAINTENANCE_END,
                        maintenanceDialogStartDate: MAINTENANCE_DIALOG_START_DATE,
                        clousureStartDate: CLOUSURE_START,
                        salesforceOutage: true
                      }}
                    >
                      <GlobalQueriesContextProvider>
                        <AuthGuard>
                          <AppLayout />
                          <InactivityDetector />
                          {/* <MissingUserInformationPopUp /> */}
                        </AuthGuard>
                      </GlobalQueriesContextProvider>
                    </Auth>
                  </Router>
                </TourProvider>
              </CHTCtheme>
            </SnackbarProvider>
          </QueryClientProvider>
        </I18nConnectedProvider>
      </Provider>
    </AppContext.Provider>
  )
}

export default App
